'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

export const getLeaveMessage = (currentPath: string) => {
  if (currentPath.startsWith('/cadastro') || currentPath.startsWith('/resumo')) {
    return 'Você está no meio de um Agendamento. Tem certeza que deseja sair? Os dados informados serão perdidos.';
  }
  if (currentPath.startsWith('/assinatura')) {
    return 'Você está no processo de Assinatura. Tem certeza que deseja sair? Todo o progresso será perdido.';
  }
  return 'Você está no meio de um processo. Tem certeza que deseja sair? Os dados informados serão perdidos e todo o progresso também.';
};

export default function NavigationGuard() {
  const pathname = usePathname();

  useEffect(() => {
    // Definimos as rotas que não queremos que o usuário saia sem querer
    const protectedRoutes = ['/cadastro', '/resumo', '/assinatura/checkout'];
    const isProtected = protectedRoutes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );

    // Se a rota atual não é protegida, não fazemos nada
    if (!isProtected) return;

    // Mensagem contextualizada
    const leaveMessage = getLeaveMessage(pathname);

    // 1. Evitar fechamento/recarregamento da aba (F5, fechar navegador)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Necessário para exibir o prompt nativo do navegador na maioria deles (o texto costuma ser ignorado por segurança, mas o valor truthy é necessário)
      e.returnValue = leaveMessage;
      return leaveMessage;
    };

    // 2. Interceptar cliques em links que mudam a página
    const handleClick = (e: MouseEvent) => {
      // Tentar achar uma âncora clicada
      const target = (e.target as Element).closest('a');
      
      // Se não for link, ignoramos o intercept global (botões programáticos são tratados em seus próprios handlers)
      if (!target) return;

      let href = target.getAttribute('href');
      if (!href) return;

      // Ignora âncoras locais ou links mailto/tel e links com _blank
      if (
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        target.getAttribute('target') === '_blank'
      ) {
        return;
      }

      try {
        const url = new URL(href, window.location.origin);

        // Trata a saída para domínios externos
        if (url.origin !== window.location.origin) {
          const confirmLeave = window.confirm(leaveMessage);
          if (!confirmLeave) {
            e.preventDefault();
            e.stopPropagation();
          }
          return;
        }

        const targetPath = url.pathname;

        // Ignora se for o mesmo caminho exatamente (ex. refesh linkado ou query params alterando)
        if (targetPath === pathname) return;

        // Permitimos navegação livre DENTRO das rotas protegidas
        // (ex. do /cadastro para /resumo) 
        // Se a rota de destino não for nenhuma protegida, emitimos o alerta
        const isTargetProtected = protectedRoutes.some(
          (route) => targetPath === route || targetPath.startsWith(`${route}/`)
        );

        if (!isTargetProtected) {
          const confirmLeave = window.confirm(leaveMessage);
          if (!confirmLeave) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      } catch (err) {
        console.error('Erro ao analisar URL no NavigationGuard:', err);
      }
    };

    // Ativa os listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    // document phase em "capture" roda logo de cara, antes do Next.js (<Link> handler)
    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [pathname]);

  return null;
}
